import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import axios from "axios";
import { toast } from "sonner";
import { 
  Trophy, 
  Flame, 
  Crown,
  Medal,
  Award,
  User,
  TrendingUp,
  Eye,
  EyeOff
} from "lucide-react";

// Helper functions for rank display
const getRankIcon = (rank) => {
  switch (rank) {
    case 1:
      return <Crown className="w-6 h-6 text-yellow-500" />;
    case 2:
      return <Medal className="w-5 h-5 text-gray-400" />;
    case 3:
      return <Award className="w-5 h-5 text-amber-600" />;
    default:
      return <span className="text-sm font-bold text-[#a69373]/50">#{rank}</span>;
  }
};

const getRankBg = (rank) => {
  switch (rank) {
    case 1:
      return "bg-gradient-to-r from-yellow-100 to-amber-100 border-yellow-300";
    case 2:
      return "bg-gradient-to-r from-gray-100 to-slate-100 border-gray-300";
    case 3:
      return "bg-gradient-to-r from-amber-50 to-orange-50 border-amber-300";
    default:
      return "bg-white border-gray-200";
  }
};

// Podium sub-component for top 3
const LeaderboardPodium = ({ leaderboard }) => {
  if (leaderboard.length < 3) return null;
  
  const positions = [
    { index: 1, pt: "pt-4", size: "w-14 h-14", iconSize: "w-7 h-7", textSize: "text-xs", gradient: "from-gray-200 to-slate-300", Icon: Medal, iconColor: "text-gray-500" },
    { index: 0, pt: "", size: "w-18 h-18", iconSize: "w-9 h-9", textSize: "text-sm font-bold", gradient: "from-yellow-300 to-amber-400 shadow-lg", Icon: Crown, iconColor: "text-yellow-700" },
    { index: 2, pt: "pt-6", size: "w-12 h-12", iconSize: "w-6 h-6", textSize: "text-xs", gradient: "from-amber-200 to-orange-300", Icon: Award, iconColor: "text-amber-700" }
  ];
  
  return (
    <div className="grid grid-cols-3 gap-2 mb-4">
      {positions.map(({ index, pt, size, iconSize, textSize, gradient, Icon, iconColor }) => {
        const entry = leaderboard[index];
        if (!entry) return null;
        return (
          <div key={entry.user_id} className={`flex flex-col items-center ${pt}`}>
            <div className={`${size} rounded-full flex items-center justify-center ${
              entry.is_current_user ? "ring-2 ring-[#7b9e7b]" : ""
            } bg-gradient-to-br ${gradient}`}>
              <Icon className={`${iconSize} ${iconColor}`} />
            </div>
            <p className={`${textSize} font-medium text-[#a69373] mt-2 text-center truncate w-full`}>
              {entry.display_name}
            </p>
            <div className="flex items-center gap-1 mt-1">
              <Flame className={index === 0 ? "w-4 h-4 text-orange-500" : "w-3 h-3 text-orange-500"} />
              <span className={index === 0 ? "text-lg font-bold text-[#a69373]" : "text-sm font-bold text-[#a69373]"}>{entry.streak}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Leaderboard entry row sub-component
const LeaderboardEntry = ({ entry }) => (
  <Card 
    className={`overflow-hidden border ${getRankBg(entry.rank)} ${
      entry.is_current_user ? "ring-2 ring-[#7b9e7b]" : ""
    }`}
    data-testid={`leaderboard-entry-${entry.rank}`}
  >
    <CardContent className="p-3 flex items-center gap-3">
      <div className="w-8 h-8 flex items-center justify-center">
        {getRankIcon(entry.rank)}
      </div>
      
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#7a9e7a] to-[#5a8a5a] flex items-center justify-center">
        <User className="w-5 h-5 text-white" />
      </div>
      
      <div className="flex-1 min-w-0">
        <p className="font-medium text-[#a69373] truncate">
          {entry.display_name}
          {entry.is_current_user && (
            <span className="text-xs text-[#5a8a5a] ml-2">(You)</span>
          )}
        </p>
        <p className="text-xs text-[#a69373]/50">
          {entry.total_checkins} total check-ins
        </p>
      </div>
      
      <div className="flex items-center gap-1 bg-orange-100 px-2 py-1 rounded-full">
        <Flame className="w-4 h-4 text-orange-500" />
        <span className="font-bold text-orange-700">{entry.streak}</span>
      </div>
    </CardContent>
  </Card>
);

export function Leaderboard() {
  const { token } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [userRank, setUserRank] = useState(null);
  const [userOnLeaderboard, setUserOnLeaderboard] = useState(false);
  const [period, setPeriod] = useState("all");
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);

  const fetchLeaderboard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/leaderboard?period=${period}&limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeaderboard(res.data.leaderboard);
      setUserRank(res.data.user_rank);
      setUserOnLeaderboard(res.data.user_on_leaderboard);
    } catch (err) {
      console.error("Failed to fetch leaderboard:", err);
    } finally {
      setLoading(false);
    }
  }, [period, token]);

  useEffect(() => {
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  const toggleOptIn = async () => {
    setToggling(true);
    try {
      const res = await axios.post(`${API}/leaderboard/opt-in`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserOnLeaderboard(res.data.show_on_leaderboard);
      toast.success(res.data.message);
      fetchLeaderboard();
    } catch (err) {
      toast.error("Failed to update preference");
    } finally {
      setToggling(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="leaderboard">
      {/* Opt-in Toggle */}
      <Card className="glass-card">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {userOnLeaderboard ? (
                <Eye className="w-5 h-5 text-[#5a8a5a]" />
              ) : (
                <EyeOff className="w-5 h-5 text-[#a69373]/40" />
              )}
              <div>
                <p className="font-medium text-[#a69373]">Show on Leaderboard</p>
                <p className="text-xs text-[#a69373]/50">
                  {userOnLeaderboard 
                    ? "Your streak is visible to the community" 
                    : "Your profile is hidden from rankings"}
                </p>
              </div>
            </div>
            <Switch
              checked={userOnLeaderboard}
              onCheckedChange={toggleOptIn}
              disabled={toggling}
              data-testid="leaderboard-toggle"
            />
          </div>
        </CardContent>
      </Card>

      {/* Period Tabs */}
      <Tabs value={period} onValueChange={setPeriod}>
        <TabsList className="grid w-full grid-cols-2 bg-[#e0ebe0]">
          <TabsTrigger 
            value="all"
            className="data-[state=active]:bg-white data-[state=active]:text-[#a69373]"
          >
            <Trophy className="w-4 h-4 mr-1" />
            All-Time Best
          </TabsTrigger>
          <TabsTrigger 
            value="weekly"
            className="data-[state=active]:bg-white data-[state=active]:text-[#a69373]"
          >
            <TrendingUp className="w-4 h-4 mr-1" />
            This Week
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Leaderboard List */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={`skeleton-${i}`} className="h-16 bg-white/50 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : leaderboard.length === 0 ? (
        <Card className="glass-card">
          <CardContent className="p-6 text-center">
            <Trophy className="w-12 h-12 text-[#d4e4d4] mx-auto mb-3" />
            <p className="text-[#a69373]/60 text-sm">
              No one has joined the leaderboard yet.
            </p>
            {!userOnLeaderboard && (
              <Button 
                className="mt-4 btn-primary"
                onClick={toggleOptIn}
                disabled={toggling}
              >
                Be the first!
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {/* Top 3 Podium */}
          <LeaderboardPodium leaderboard={leaderboard} />

          {/* Rest of Leaderboard */}
          {leaderboard.slice(3).map((entry) => (
            <LeaderboardEntry key={entry.user_id} entry={entry} />
          ))}

          {/* User's rank if not in top 10 */}
          {userOnLeaderboard && userRank && userRank > 10 && (
            <Card className="glass-card mt-4">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#7a9e7a] to-[#5a8a5a] flex items-center justify-center">
                      <User className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-medium text-[#a69373]">Your Rank</p>
                      <p className="text-xs text-[#a69373]/50">Keep up the streak!</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-heading text-[#a69373]">#{userRank}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

export default Leaderboard;
